// components/FilePreviewCard.tsx

import { useAppTheme } from "core/contexts/theme/theme";
import { useEffect, useState } from "react";
import styled from "styled-components";

export const FilePreviewCardStyles = styled("div")`
  border: none !important;
  filter: drop-shadow(4px 8px 64px hsla(291deg, 50%, 19%, 0.5));
  box-shadow: 2px 2px 4.2px hsl(0deg 0% 0% / 0.17) !important;
  border-radius: 7px;
  // width: max-content;
  max-width: 40%;
  width: calc(100% + 10px);
  background-color: ${({ theme }) =>
      theme.name === "dark-theme" ? "rgba(85, 85, 85, .2)" : ""};

  img {
    border-radius: 7px;
  }

  button {
    width: 20px;
  }

  div {
    color: ${({ theme }) => theme.colors.textCdarkolor};
  }
`;

export const FilePreviewCard = ({
  file,
  onRemove,
}: {
  file: File;
  onRemove: () => void;
}) => {
  const isImage = file.type.startsWith("image/");
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  const [isUploading, setIsUploading] = useState(true);

  const [progress, setProgress] = useState(0);

  const { apptheme } = useAppTheme();

  const isDarkTheme = apptheme?.name === "dark-theme";

  useEffect(() => {
    let interval;

    if (progress < 100) {
      interval = setInterval(() => {
        setProgress((prev) => {
          // Increment progress by 2% or smaller steps for smoothness
          const next = prev + 2;
          return next > 100 ? 100 : next;
        });
      }, 70); // about 70ms for smooth progress, adjust as needed
    }

    return () => clearInterval(interval);
  }, [progress]);

  useEffect(() => {
    if (isImage) {
      const reader = new FileReader();
      reader.onload = (e) => setImageUrl(e.target?.result as string);
      reader.readAsDataURL(file);
    }
  }, [file]);

  const radius = 16;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;
  const textclr = isDarkTheme ? "text-white" : "text-zinc-900";

  return (
    <FilePreviewCardStyles
      className={`relative 
        ${isImage ? "w-[120px]" : "w-[290px]"} 
        ${!isImage && "p-2"}
        bg-gray-100 flex items-center gap-2 shadow-sm m-2 border`}
    >
      {/* Uploading Widget */}
      {isImage && imageUrl? (
        <>
          {/* Show progress ring on top of image */}
          {progress < 100 && (
            <div className="absolute inset-0 flex items-center justify-center z-10 bg-white/70 rounded">
              <svg
                className="w-12 h-12"
                viewBox="0 0 40 40"
              >
                <circle
                  className="text-gray-300"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="transparent"
                  r={radius}
                  cx="20"
                  cy="20"
                />
                <circle
                  className="text-blue-500"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="transparent"
                  r={radius}
                  cx="20"
                  cy="20"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  strokeLinecap="round"
                  style={{ transition: "stroke-dashoffset 0.1s linear" }}
                />
              </svg>
              <div className="absolute text-sm font-bold text-gray-700">{progress}%</div>
            </div>
          )}
          <img
            src={imageUrl}
            alt="preview"
            className={`object-cover w-full h-full ${progress < 100 ? "opacity-40" : "opacity-100"}`}
          />
        </>
      ) : (
        <div className="min-w-[50px]">
          {progress < 100 ? (
            <div className="w-12 h-12 relative mx-auto">
              <svg
                className="w-12 h-12"
                viewBox="0 0 40 40"
              >
                <circle
                  className="text-gray-300"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="transparent"
                  r={radius}
                  cx="20"
                  cy="20"
                />
                <circle
                  className="text-blue-500"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="transparent"
                  r={radius}
                  cx="20"
                  cy="20"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  strokeLinecap="round"
                  style={{ transition: "stroke-dashoffset 0.1s linear" }}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center font-bold text-black">{progress}%</div>
            </div>
          ) : (
            <div className="bg-blue-300 text-white rounded flex items-center justify-center text-sm font-bold p-2 ">
              {file.name.split(".").pop()?.toUpperCase()}
            </div>
          )}
        </div>
      )}

      {/* File name and size */}
      <div className="flex-1 overflow-hidden">
        <div className={`font-bold text-sm truncate p-1 ${textclr}`}>{file.name}</div>
        <div className="text-xs text-gray-500">
          {(file.size / 1024).toFixed(1)} KB
        </div>
      </div>

      {/* Remove button */}
      <button
        onClick={onRemove}
        className="absolute top-2 right-2 bg-white/80 hover:bg-red-400 hover:text-white text-black p-[2px] text-xs font-bold"
        title="Remove"
      >
        ✕
      </button>
    </FilePreviewCardStyles>
  );
};
